import React, { useMemo, useState } from "react";
import { Checkbox, FormControlLabel, IconButton, Tooltip, Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel } from "@material-ui/core";
import { PersonAdd, Refresh } from "@material-ui/icons";
import GridContainer from "../../components/Grid/GridContainer";
import GridItem from "../../components/Grid/GridItem";
import Button from "../../components/CustomButtons/Button";
import CustomDialogWithContact from "components/CustomDialog/CustomDialogWithContact";
import axiosGci from "store/axios/axios-gci";
import { formatUTCtoPST, handleError } from "../../shared/utility";

const money = value => {
    if (value == null || value === "") return "";
    const parsed = Number(String(value).replace("$", "").split(",").join(""));
    if (Number.isNaN(parsed)) return value;
    return parsed.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
};

const percent = value => {
    if (value == null || value === "") return "";
    const parsed = Number(value);
    if (Number.isNaN(parsed)) return value;
    return `${(parsed * 100).toFixed(2)}%`;
};

const formatDateMDY = value => {
    if (!value) return "";
    const raw = String(value).split("T")[0].split(" ")[0];
    const parts = raw.split("-");
    if (parts.length === 3) return [parts[1], parts[2], parts[0]].join("/");
    return raw;
};

const displayName = contact => {
    if (!contact) return "";
    return [contact.first_name || contact.name, contact.last_name]
        .filter(Boolean)
        .join(" ");
};

const normalizeSelectedContact = contactData => {
    let contact = contactData;
    if (contact && contact.signers && contact.signers.length > 0) {
        contact = {
            ...contact.signers[0],
            connect_user_id: contact.signers[0].connect_user_id || contact.connect_user_id
        };
    }
    return contact;
};

const getIlliUserIdFromContact = contact => {
    if (!contact) return null;
    return contact.connect_user_id || contact.illi_user_id || contact.user_id || null;
};

const numericColumns = [
    "payment_report_id",
    "payment_id",
    "rs_id",
    "payment_amount",
    "side_amount",
    "assigned_gci_amount",
    "level_number",
    "payout_rate",
    "associate_payout_amount",
    "house_amount"
];

const getSortValue = (row, key) => {
    if (key === "processed_at") {
        const value = row.processed_at || row.created_at;
        return value ? new Date(value).getTime() : null;
    }
    if (key === "payment_date") {
        const value = row.payment_received_date || row.deposit_date || row.payment_date;
        return value ? new Date(value).getTime() : null;
    }
    if (key === "period_name") return row.period_name || "N/A";
    if (key === "run_status") return row.gci_run_status || "";

    const value = row[key];
    if (numericColumns.includes(key)) {
        if (value == null || value === "") return null;
        const parsed = Number(String(value).replace(/[$,%]/g, "").split(",").join(""));
        return Number.isNaN(parsed) ? null : parsed;
    }
    return value == null ? "" : String(value).toLowerCase();
};

const GCIAssociatePayoutHistory = () => {
    const [selectedAssociate, setSelectedAssociate] = useState(null);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sortKey, setSortKey] = useState("processed_at");
    const [sortDirection, setSortDirection] = useState("desc");
    const [optionalColumns, setOptionalColumns] = useState({
        period_name: false,
        run_status: false,
        entry_type: false
    });

    const totals = useMemo(() => rows.reduce((all, row) => ({
        assigned: all.assigned + Number(row.assigned_gci_amount || 0),
        payout: all.payout + Number(row.associate_payout_amount || 0),
        house: all.house + Number(row.house_amount || 0)
    }), { assigned: 0, payout: 0, house: 0 }), [rows]);

    const loadHistory = async associate => {
        if (!associate || !associate.id) return;
        try {
            setLoading(true);
            const response = await axiosGci.get(`/associates/${associate.id}/gci-ledger`);
            setRows(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            handleError(error);
            alert("Could not load associate payout history.");
        } finally {
            setLoading(false);
        }
    };

    const chooseAssociate = (contactData) => {
        const contact = normalizeSelectedContact(contactData);
        if (!contact || !contact.id) return;
        const illiUserId = getIlliUserIdFromContact(contact);
        if (!illiUserId) {
            alert("Selected contact is not connected to an illi user, so payout history cannot be loaded.");
            return;
        }
        const associate = {
            id: illiUserId,
            contact_id: contact.id,
            name: displayName(contact) || `Associate ${illiUserId}`,
            raw: contact
        };
        setSelectedAssociate(associate);
        setPickerOpen(false);
        loadHistory(associate);
    };

    const sortedRows = useMemo(() => [...rows].sort((left, right) => {
        const leftValue = getSortValue(left, sortKey);
        const rightValue = getSortValue(right, sortKey);

        if (leftValue == null && rightValue == null) return 0;
        if (leftValue == null) return 1;
        if (rightValue == null) return -1;

        const comparison = typeof leftValue === "number" && typeof rightValue === "number"
            ? leftValue - rightValue
            : String(leftValue).localeCompare(String(rightValue), undefined, { numeric: true, sensitivity: "base" });
        return sortDirection === "asc" ? comparison : -comparison;
    }), [rows, sortKey, sortDirection]);

    const tableData = sortedRows.map(row => ({
        payment_report_id: row.payment_report_id || "",
        processed_at: row.processed_at ? formatUTCtoPST(row.processed_at) : formatUTCtoPST(row.created_at),
        period_name: row.period_name || "N/A",
        payment_id: row.payment_id || "",
        payment_date: formatDateMDY(row.payment_received_date || row.deposit_date || row.payment_date),
        invoice_num: row.invoice_num || "",
        rs_id: row.rs_id || "",
        project_name: row.project_name || "",
        side: row.side || "",
        payment_amount: money(row.payment_amount),
        side_amount: money(row.side_amount),
        assigned_gci_amount: money(row.assigned_gci_amount),
        level_number: row.level_number == null ? "" : row.level_number,
        payout_rate: percent(row.payout_rate),
        associate_payout_amount: money(row.associate_payout_amount),
        house_amount: money(row.house_amount),
        run_status: row.gci_run_status || "",
        entry_type: row.entry_type || ""
    }));

    const columns = [
        { label: "Report ID", key: "payment_report_id" },
        { label: "Processed", key: "processed_at" },
        { label: "Period", key: "period_name" },
        { label: "Payment ID", key: "payment_id" },
        { label: "Payment Date", key: "payment_date" },
        { label: "Invoice #", key: "invoice_num" },
        { label: "RS ID", key: "rs_id" },
        { label: "Project", key: "project_name" },
        { label: "Side", key: "side" },
        { label: "Payment", key: "payment_amount", align: "right" },
        { label: "Side Amount", key: "side_amount", align: "right" },
        { label: "Assigned GCI", key: "assigned_gci_amount", align: "right" },
        { label: "Level", key: "level_number", align: "right" },
        { label: "Rate", key: "payout_rate", align: "right" },
        { label: "Payout", key: "associate_payout_amount", align: "right" },
        { label: "House", key: "house_amount", align: "right" },
        { label: "Run Status", key: "run_status" },
        { label: "Entry", key: "entry_type" }
    ];

    const visibleColumns = columns.filter(column => (
        optionalColumns[column.key] === undefined || optionalColumns[column.key]
    ));

    const toggleOptionalColumn = key => {
        setOptionalColumns({
            ...optionalColumns,
            [key]: !optionalColumns[key]
        });
    };

    const sortBy = key => {
        if (sortKey === key) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
            return;
        }
        setSortKey(key);
        setSortDirection("asc");
    };

    return (
        <GridContainer>
            <GridItem xs={12} sm={12} md={12}>
                <h1>Associate Payout History</h1>
            </GridItem>

            <GridItem xs={12} sm={12} md={12}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 18 }}>
                    <Button color="primary" onClick={() => setPickerOpen(true)}>
                        <PersonAdd style={{ marginRight: 8 }} /> Select Associate
                    </Button>
                    {selectedAssociate && (
                        <div style={{ fontSize: 16 }}>
                            <strong>{selectedAssociate.name}</strong>
                            <span style={{ color: "#777", marginLeft: 8 }}>illi User ID #{selectedAssociate.id}</span>
                            {selectedAssociate.contact_id && (
                                <span style={{ color: "#999", marginLeft: 8 }}>Contact ID #{selectedAssociate.contact_id}</span>
                            )}
                        </div>
                    )}
                    {selectedAssociate && (
                        <Tooltip title="Refresh payout history">
                            <span>
                                <IconButton disabled={loading} onClick={() => loadHistory(selectedAssociate)}>
                                    <Refresh style={loading ? { animation: "gci-payout-spin 0.9s linear infinite" } : {}} />
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}
                    <style>{"@keyframes gci-payout-spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}"}</style>
                </div>
            </GridItem>

            {selectedAssociate && (
                <GridItem xs={12} sm={12} md={12}>
                    <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 18 }}>
                        <div style={{ padding: "12px 16px", border: "1px solid #eee", borderRadius: 6, minWidth: 170 }}>
                            <div style={{ color: "#777", fontSize: 12 }}>Assigned GCI</div>
                            <strong>{money(totals.assigned)}</strong>
                        </div>
                        <div style={{ padding: "12px 16px", border: "1px solid #eee", borderRadius: 6, minWidth: 170 }}>
                            <div style={{ color: "#777", fontSize: 12 }}>Associate Payout</div>
                            <strong>{money(totals.payout)}</strong>
                        </div>
                        <div style={{ padding: "12px 16px", border: "1px solid #eee", borderRadius: 6, minWidth: 170 }}>
                            <div style={{ color: "#777", fontSize: 12 }}>House Amount</div>
                            <strong>{money(totals.house)}</strong>
                        </div>
                    </div>
                </GridItem>
            )}

            <GridItem xs={12} sm={12} md={12}>
                {!selectedAssociate ? (
                    <div style={{ padding: 24, border: "1px solid #eee", borderRadius: 6, color: "#666" }}>
                        Select an associate to view payout history.
                    </div>
                ) : (
                    <div style={{ overflowX: "auto", border: "1px solid #eee", borderRadius: 6 }}>
                        <div style={{ padding: "8px 12px", borderBottom: "1px solid #eee", whiteSpace: "nowrap" }}>
                            <span style={{ color: "#666", fontSize: 12, marginRight: 10 }}>Optional columns:</span>
                            <FormControlLabel
                                control={<Checkbox checked={optionalColumns.period_name} onChange={() => toggleOptionalColumn("period_name")} />}
                                label="Period"
                            />
                            <FormControlLabel
                                control={<Checkbox checked={optionalColumns.run_status} onChange={() => toggleOptionalColumn("run_status")} />}
                                label="Run Status"
                            />
                            <FormControlLabel
                                control={<Checkbox checked={optionalColumns.entry_type} onChange={() => toggleOptionalColumn("entry_type")} />}
                                label="Entry"
                            />
                        </div>
                        {loading && (
                            <div style={{ padding: 16, color: "#666" }}>Loading payout history...</div>
                        )}
                        <Table>
                            <TableHead>
                                <TableRow>
                                    {visibleColumns.map(column => (
                                        <TableCell
                                            key={column.key}
                                            align={column.align || "left"}
                                            style={{ fontWeight: 600 }}
                                        >
                                            <TableSortLabel
                                                active={sortKey === column.key}
                                                direction={sortKey === column.key ? sortDirection : "asc"}
                                                onClick={() => sortBy(column.key)}
                                            >
                                                {column.label}
                                            </TableSortLabel>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {tableData.length === 0 && !loading && (
                                    <TableRow>
                                        <TableCell colSpan={visibleColumns.length} style={{ color: "#666", padding: 18 }}>
                                            No payout history found for this associate.
                                        </TableCell>
                                    </TableRow>
                                )}
                                {tableData.map((row, rowIndex) => (
                                    <TableRow key={`associate-payout-row-${row.payment_report_id || "ledger"}-${row.payment_id || rowIndex}-${rowIndex}`}>
                                        {visibleColumns.map(column => (
                                            <TableCell
                                                key={column.key}
                                                align={column.align || "left"}
                                            >
                                                {row[column.key]}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </GridItem>

            {pickerOpen && (
                <CustomDialogWithContact
                    open={true}
                    close={() => setPickerOpen(false)}
                    choose={chooseAssociate}
                    create_and_choose={chooseAssociate}
                    peopleType="agents"
                    title="Select Associate"
                    label="Search Associates"
                />
            )}
        </GridContainer>
    );
};

export default GCIAssociatePayoutHistory;
